import React, {useMemo} from "react";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";
import {SelectButton} from "primereact/selectbutton";
import {Tag} from "primereact/tag";

function entityKey(value) {
    if (value == null) return null;
    if (typeof value === "string" || typeof value === "number") return value;
    if (value.data) return entityKey(value.data);
    return value.documentId ?? value.id ?? null;
}

function entityIdentifiers(value) {
    if (value == null) return [];
    if (typeof value === "string" || typeof value === "number") return [String(value)];
    if (value.data) return entityIdentifiers(value.data);
    return [...new Set([value.documentId, value.id].filter((item) => item != null).map(String))];
}

function sameEntity(first, second) {
    const secondIds = new Set(entityIdentifiers(second));
    return entityIdentifiers(first).some((identifier) => secondIds.has(identifier));
}

function integerValue(value) {
    if (value === null || value === undefined || value === "") return null;
    try {
        const normalized = String(value).trim();
        if (!/^-?\d+(?:\.0+)?$/.test(normalized)) return null;
        return window.BigInt(normalized.split(".")[0]);
    } catch (error) {
        return null;
    }
}

function formatNumber(value) {
    const number = integerValue(value);
    return number === null ? "-" : new Intl.NumberFormat("fa-IR").format(number);
}

function translatedValue(translate, prefix, value) {
    if (!value) return "-";
    const key = `${prefix}.${String(value).toLowerCase()}`;
    const translated = translate(key);
    return translated === key ? translate("commerce.value.unknown") : translated;
}

function normalizeFeature(feature) {
    if (typeof feature === "string" || typeof feature === "number") return String(feature);
    return feature?.title || feature?.name || feature?.key || null;
}

function planFeatures(plan) {
    return [...new Set([
        ...(Array.isArray(plan?.features) ? plan.features : []),
        ...(Array.isArray(plan?.entitlements) ? plan.entitlements : []),
    ].map(normalizeFeature).filter(Boolean))];
}

function catalogEntitlement(plan, key) {
    return (plan?.entitlements || []).find((entitlement) => entitlement?.feature?.key === key) || null;
}

function catalogEntitlementValue(entitlement) {
    if (!entitlement || entitlement.isUnlimited) return null;
    return entitlement.value ?? null;
}

function catalogFeatures(plan) {
    const marketingFeatures = (plan?.marketingFeatures || []).map((feature) => (
        feature.displayValue ? `${feature.title}: ${feature.displayValue}` : feature.title
    ));
    const entitlementFeatures = (plan?.entitlements || []).map((entitlement) => {
        const name = entitlement?.feature?.name;
        if (!name) return null;
        return entitlement.displayValue ? `${name}: ${entitlement.displayValue}` : name;
    });
    return [...new Set([...marketingFeatures, ...entitlementFeatures].filter(Boolean))];
}

function catalogPriceAmount(price) {
    return price?.displayAmount ?? (price?.seatPricingMode === "per_seat" ? price?.pricePerExtraSeat : price?.amount);
}

export function catalogPeriodKey(price) {
    return `${price?.billingInterval || "none"}:${Number(price?.intervalCount || 1)}`;
}

export function catalogPeriodLabel(price, translate) {
    if (price?.billingInterval === "year" && Number(price?.intervalCount || 1) === 1) {
        return translate("billing.period.annual");
    }
    return `${formatNumber(price?.intervalCount || 1)} ${translate(`billing.interval.${price?.billingInterval || "month"}-unit`)}`;
}

export function projectSubscriptionCatalog(catalog) {
    const products = [];
    const plans = [];

    (catalog || []).forEach((subscriptionPlan) => {
        const memberLimit = catalogEntitlement(subscriptionPlan, "members.internal.active.limit");
        const storageLimit = catalogEntitlement(subscriptionPlan, "storage.workspace.bytes");
        const creditPackage = (subscriptionPlan.prices || []).some((price) => price.billingType === "one_time");
        const product = {
            id: subscriptionPlan.id,
            documentId: subscriptionPlan.documentId,
            name: subscriptionPlan.name,
            type: creditPackage ? "credit_pack" : "subscription",
            active: true,
            defaultStorageQuotaBytes: catalogEntitlementValue(storageLimit),
            sortOrder: subscriptionPlan.sortOrder,
            salePlans: [],
        };

        (subscriptionPlan.prices || []).forEach((price) => {
            const intervalUnit = ["month", "quarter", "year"].includes(price.billingInterval)
                ? price.billingInterval
                : "none";
            const projectedPlan = {
                id: price.id,
                documentId: price.documentId,
                name: subscriptionPlan.name,
                description: subscriptionPlan.description,
                marketingSummary: subscriptionPlan.marketingSummary,
                badge: subscriptionPlan.badge,
                trialEnabled: subscriptionPlan.trialEnabled === true,
                trialDays: Number(subscriptionPlan.trialDays || 0),
                pricingModel: subscriptionPlan.pricingModel,
                salesMode: subscriptionPlan.salesMode,
                initialGiftCredit: subscriptionPlan.initialGiftCredit,
                billingType: price.billingType,
                intervalUnit,
                intervalCount: price.intervalCount || 1,
                periodKey: catalogPeriodKey(price),
                currency: price.displayCurrencyUnit || price.currency,
                canonicalCurrency: price.currency,
                priceAmount: catalogPriceAmount(price),
                price: catalogPriceAmount(price),
                displayMonthlyAmount: price.monthlyEquivalentAmount,
                displayTotalAmount: price.displayTotalAmount,
                displayEffectivePerSeatMonthlyAmount: price.displayEffectivePerSeatMonthlyAmount,
                savingsPercentage: price.savingsPercentage,
                maxUsers: price.capacity ?? catalogEntitlementValue(memberLimit),
                capacity: price.capacity ?? price.includedSeats,
                monthlyAiAllocation: price.monthlyAiAllocation,
                creditsIncluded: creditPackage ? Number(price.monthlyAiAllocation || 0) : null,
                annualDiscountPercentage: price.annualDiscountPercentage,
                storageQuotaBytes: catalogEntitlementValue(storageLimit),
                features: catalogFeatures(subscriptionPlan),
                customizations: Array.isArray(subscriptionPlan.customizations) ? subscriptionPlan.customizations : [],
                entitlements: [],
                isFeatured: subscriptionPlan.recommended === true,
                isActive: price.isActive !== false,
                selectable: price.selectable !== false
                    && price.billingType !== "free"
                    && (subscriptionPlan.salesMode === "checkout" || price.billingType === "contractual"),
                sortOrder: (Number(subscriptionPlan.sortOrder || 0) * 1000) + Number(price.sortOrder || 0),
                product,
                subscriptionPlan,
                planPrice: price,
            };
            product.salePlans.push(projectedPlan);
            plans.push(projectedPlan);
        });

        products.push(product);
    });

    return {products, plans};
}

export function findPlanPriceForInterval(plan, interval) {
    const prices = (plan?.prices || []).filter((price) => price.isActive !== false);
    return prices.find((price) => price.billingType === "recurring" && catalogPeriodKey(price) === interval)
        || prices.find((price) => price.billingType === "contractual" && price.billingInterval === interval)
        || prices.find((price) => price.billingType === "free")
        || prices.find((price) => price.billingType === "contractual")
        || prices.find((price) => price.isDefault)
        || prices[0]
        || null;
}

export function groupSubscriptionPlansForInterval(plans, interval) {
    const grouped = new Map();
    plans.forEach((plan) => {
        const key = String(entityKey(plan.subscriptionPlan) || entityKey(plan.product) || plan.name);
        grouped.set(key, [...(grouped.get(key) || []), plan]);
    });
    return [...grouped.values()].map((prices) => {
        const recurring = prices.filter((plan) => (
            plan.planPrice?.billingType === "recurring"
            && plan.periodKey === interval
        ));
        const contractual = prices.filter((plan) => plan.planPrice?.billingType === "contractual");
        const free = prices.filter((plan) => plan.planPrice?.billingType === "free");
        const visible = recurring.length ? recurring : (free.length ? free : contractual);
        return visible.sort((first, second) => (
            Number(first.capacity ?? Number.MAX_SAFE_INTEGER) - Number(second.capacity ?? Number.MAX_SAFE_INTEGER)
        ));
    }).filter((group) => group.length);
}

export function subscriptionPlanSelectable(plan, freeSelectionEnabled = false) {
    const free = plan?.billingType === "free"
        || integerValue(plan?.priceAmount ?? plan?.price) === window.BigInt(0);
    return free ? freeSelectionEnabled : plan?.selectable !== false;
}

function SubscriptionPlanCard({plan, tierPlans, selected, loading, freeSelectionEnabled, onSelect, translate}) {
    const features = planFeatures(plan);
    const price = plan.priceAmount ?? plan.price;
    const actualPrice = plan.priceAmount ?? plan.price;
    const contractual = plan.billingType === "contractual";
    const free = plan.billingType === "free" || integerValue(actualPrice) === window.BigInt(0);
    const selectable = subscriptionPlanSelectable(plan, freeSelectionEnabled);
    const highlighted = plan.isFeatured || selected;
    const selectedClass = free
        ? selected
            ? "border-2 border-green-500 bg-green-50 shadow-3"
            : "border-1 border-green-200 bg-green-50 shadow-none"
        : highlighted
            ? "border-2 border-primary surface-0 shadow-3"
            : "border-1 border-200 surface-0 shadow-none";
    const buttonLabel = free
        ? translate(freeSelectionEnabled ? "billing.action.choose-free" : "billing.action.free-automatic")
        : contractual
        ? translate("billing.action.contact-sales")
        : `${translate("billing.action.choose")} ${plan.name}`;
    const capacityOptions = (tierPlans || []).map((tierPlan) => ({
        label: `${formatNumber(tierPlan.capacity)} ${translate("commerce.subscription.members")}`,
        value: String(entityKey(tierPlan)),
        plan: tierPlan,
    }));

    return (
        <Card
            className={`w-full h-full relative border-round-3xl ${selectedClass}`}
            pt={{body: {className: "h-full p-3"}, content: {className: "p-0 h-full"}}}
        >
            {plan.isFeatured && (
                <Tag
                    className="absolute left-50 top-0 text-xs border-round-3xl white-space-nowrap"
                    severity="warning"
                    value={plan.badge || translate("billing.plan.recommended-choice")}
                    pt={{root: {style: {transform: "translate(-50%, -50%)"}}}}
                />
            )}
            <div className="flex flex-column h-full text-right">
                <div className="h-3rem overflow-hidden">
                    {plan.marketingSummary && <span className="text-xs text-500 line-height-2">{plan.marketingSummary}</span>}
                </div>
                <div className="h-2rem flex align-items-center">
                    <span className={`text-xl font-bold ${free ? "text-green-800" : "text-900"}`}>{plan.name}</span>
                </div>
                <div className="h-4rem overflow-hidden">
                    {plan.description && <span className="text-xs text-600 line-height-2">{plan.description}</span>}
                </div>

                <div className="h-8rem flex flex-column justify-content-center gap-1 my-2 overflow-hidden">
                    {contractual ? (
                        <span className="text-2xl font-bold text-900">{translate("billing.value.contractual")}</span>
                    ) : (
                        <div className="flex align-items-baseline gap-2">
                            <span className="text-2xl font-bold text-900">{formatNumber(free ? 0 : price)}</span>
                            <span className="text-xs text-600">
                                {translatedValue(translate, "commerce.currency", plan.currency || "IRR")}
                                {plan.planPrice?.seatPricingMode === "per_seat" && ` / ${translate("commerce.subscription.each-member")}`}
                                {!free && ` / ${catalogPeriodLabel(plan.planPrice, translate)}`}
                            </span>
                        </div>
                    )}
                    {!free && !contractual && plan.displayTotalAmount != null && plan.planPrice?.seatPricingMode === "per_seat" && <span className="text-xs text-500 line-height-2">{translate("commerce.subscription.total-for-capacity")}: {formatNumber(plan.displayTotalAmount)} {translatedValue(translate, "commerce.currency", plan.currency || "IRR")}</span>}
                    {!free && !contractual && plan.displayEffectivePerSeatMonthlyAmount != null && <span className="text-xs text-500 line-height-2">{translate("commerce.subscription.per-member-monthly")}: {formatNumber(plan.displayEffectivePerSeatMonthlyAmount)} {translatedValue(translate, "commerce.currency", plan.currency || "IRR")}</span>}
                    {!free && !contractual && Number(plan.planPrice?.annualDiscountPercentage || 0) > 0 && (
                        <span className="text-xs text-500 line-height-2">
                            {translate("billing.plan.display-discount")}: {formatNumber(plan.planPrice.annualDiscountPercentage)}%
                        </span>
                    )}
                    {!contractual && plan.capacity != null && <span className="text-xs text-500 line-height-2">{translate("commerce.subscription.capacity")}: {formatNumber(plan.capacity)}</span>}
                    {!contractual && plan.monthlyAiAllocation != null && <span className="text-xs text-500 line-height-2">{translate("billing.plan.monthly-ai")}: {formatNumber(plan.monthlyAiAllocation)}</span>}
                </div>

                <div className="h-5rem flex flex-column gap-2 mb-3">
                    {!free && !contractual && capacityOptions.length > 1 && (
                        <>
                        <label className="text-xs font-bold text-700" htmlFor={`plan-capacity-${entityKey(plan.subscriptionPlan)}`}>
                            {translate("commerce.subscription.member-capacity")}
                        </label>
                        <Dropdown
                            inputId={`plan-capacity-${entityKey(plan.subscriptionPlan)}`}
                            className="w-full h-3rem text-xs"
                            panelClassName="rtl text-xs"
                            value={String(entityKey(plan))}
                            options={capacityOptions}
                            optionLabel="label"
                            optionValue="value"
                            disabled={loading}
                            onChange={(event) => {
                                const option = capacityOptions.find((candidate) => candidate.value === event.value);
                                if (option) onSelect(option.plan);
                            }}
                        />
                        </>
                    )}
                </div>

                <Button
                    type="button"
                    className="w-full h-3rem flex-row-reverse justify-content-center text-xs gap-2 border-round-xl mb-3"
                    icon={free && !freeSelectionEnabled ? "pi pi-lock" : contractual ? "pi pi-comments" : "pi pi-arrow-left"}
                    iconPos="left"
                    label={buttonLabel}
                    outlined={!highlighted}
                    severity={free ? "success" : highlighted ? undefined : "secondary"}
                    loading={selected && loading}
                    disabled={!selectable || (loading && !selected)}
                    onClick={() => selectable && onSelect(plan)}
                />

                <div className="flex flex-column gap-2">
                    {(features.length ? features.slice(0, 3) : [translate("commerce.subscription.all-features")]).map((feature, index) => (
                        <div className="flex align-items-start gap-2 text-xs text-700" key={`${entityKey(plan)}-subscription-feature-${index}`}>
                            <i className={`pi pi-check ${free ? "text-green-600" : "text-cyan-500"}`}/>
                            <span className="line-height-2">{feature}</span>
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
}

export function SubscriptionPlanSelector({
    plans,
    interval,
    selectedPlan,
    loading,
    onIntervalChange,
    onSelect,
    translate,
    emptyText,
    freeSelectionEnabled = false,
}) {
    const activePlans = useMemo(
        () => (plans || []).filter((plan) => plan.isActive !== false),
        [plans]
    );
    const visiblePlanGroups = useMemo(
        () => groupSubscriptionPlansForInterval(activePlans, interval),
        [activePlans, interval]
    );
    const intervalOptions = useMemo(() => {
        const byPeriod = new Map();
        activePlans.filter((plan) => plan.billingType === "recurring").forEach((plan) => {
            if (byPeriod.has(plan.periodKey)) return;
            byPeriod.set(plan.periodKey, {
                label: catalogPeriodLabel(plan.planPrice, translate),
                value: plan.periodKey,
                hint: Number(plan.annualDiscountPercentage || 0) > 0
                    ? `${formatNumber(plan.annualDiscountPercentage)}% ${translate("billing.value.less")}`
                    : "",
            });
        });
        return [...byPeriod.values()];
    }, [activePlans, translate]);

    return (
        <>
            <div className="flex justify-content-center">
                <SelectButton
                    className="rtl flex w-16rem h-4rem surface-100 border-1 border-200 border-round-2xl p-1"
                    value={interval}
                    options={intervalOptions}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                    itemTemplate={(option) => (
                        <span className="rtl flex align-items-center justify-content-center gap-2 white-space-nowrap">
                            <span className="text-sm">{option.label}</span>
                            {option.hint && <span className="text-xs text-green-600">{option.hint}</span>}
                        </span>
                    )}
                    pt={{
                        button: ({context}) => ({
                            className: `h-full flex-1 justify-content-center border-none border-round-xl px-3 py-2 ${
                                context.selected
                                    ? "surface-0 text-900 shadow-2"
                                    : "surface-100 text-600 shadow-none"
                            }`,
                        }),
                    }}
                    onChange={(event) => onIntervalChange(event.value)}
                />
            </div>

            {!visiblePlanGroups.length ? (
                <Message
                    className="w-full text-xs"
                    severity="info"
                    text={emptyText || translate("billing.action.unavailable")}
                    pt={{text: {className: "text-xs"}}}
                />
            ) : (
                <div className="grid align-items-stretch justify-content-center mt-2">
                    {visiblePlanGroups.map((tierPlans) => {
                        const selectedTier = tierPlans.find((candidate) => sameEntity(selectedPlan, candidate));
                        const plan = selectedTier
                            || tierPlans.find((candidate) => candidate.planPrice?.isDefault)
                            || tierPlans[0];
                        const planSelected = tierPlans.some((candidate) => sameEntity(selectedPlan, candidate));
                        return (
                        <div className="col-12 md:col-6 xl:col-3 flex" key={entityKey(plan.subscriptionPlan) || entityKey(plan)}>
                            <SubscriptionPlanCard
                                plan={plan}
                                tierPlans={tierPlans}
                                selected={planSelected}
                                loading={loading}
                                freeSelectionEnabled={freeSelectionEnabled}
                                onSelect={onSelect}
                                translate={translate}
                            />
                        </div>
                        );
                    })}
                </div>
            )}
            <span className="text-xs text-500 text-center line-height-2">{translate("billing.pricing.period-note")}</span>
        </>
    );
}
